// PLEX-6361: which conversations in a history list have an agent still working on them,
// for the icon the lists show next to their titles. Shared by the v2 chat rail, the v2
// recent-chats lists and the Classic history drawer.
import { useEffect, useRef, useState } from "react";
import { fetchTurnRunning } from "../queries/search";
import { isConversationId, RUNNING_TURN_POLL_MS } from "./liveTurn";

// At most this many records are asked about per list. Admission control caps a user's
// concurrent agents well below it, so only records left behind by crashed turns add up.
export const MAX_PROBED = 10;

/**
 * The ids worth asking about: records the backend marks `inProgress` (PLEX-6087 S2), in list
 * order. A record whose worker died mid-turn keeps the flag, so a candidate isn't yet running.
 */
export const runningCandidates = (items = [], limit = MAX_PROBED) =>
  (items || [])
    .filter((item) => item?.inProgress === true && isConversationId(item.id))
    .map((item) => item.id)
    .slice(0, limit);

/**
 * The ids of `items` whose agent is still working. Each candidate's stream is asked
 * (fetchTurnRunning) and only a definite yes counts, so a crashed turn shows no icon. Running
 * ones are asked again every RUNNING_TURN_POLL_MS; an unanswered poll keeps them running.
 * `onEnded(ids)` is called with the ones that have since finished, so the caller can reload
 * the list for their final title, snippet and time.
 */
export const useRunningConversations = (items, { auth, onEnded } = {}) => {
  const key = runningCandidates(items).join(",");
  const [running, setRunning] = useState(() => new Set());
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    if (!auth || !key) {
      setRunning((prev) => (prev.size ? new Set() : prev));
      return undefined;
    }
    let stopped = false;
    let timer = null;
    let live = new Set();
    const ask = async (ids) => {
      const answers = await Promise.all(ids.map((id) => fetchTurnRunning(id, auth)));
      if (stopped) return;
      const next = new Set(ids.filter((id, i) => answers[i] === true || (answers[i] == null && live.has(id))));
      const ended = ids.filter((id, i) => answers[i] === false && live.has(id));
      live = next;
      setRunning(next);
      if (ended.length) onEndedRef.current?.(ended);
      if (live.size) timer = setTimeout(() => ask([...live]), RUNNING_TURN_POLL_MS);
    };
    ask(key.split(","));
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [key, auth]);

  return running;
};
